import { Dialog, useDialogStore } from '@ariakit/react';
import { addTag } from '@ben/columbia/api';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import Input from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ForwardedRef } from 'react';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';

const TAG_NAME_REQUIRED = 'Tag name is required';

const AddTagModal = forwardRef((_props, ref: ForwardedRef<unknown>) => {
  const dialog = useDialogStore();
  const queryClient = useQueryClient();
  const [tagName, setTagName] = useState<string>('');
  const [tagNameError, setTagNameError] = useState<string | undefined>();

  useImperativeHandle(ref, () => {
    return {
      showModal: (initialTagName: string) => {
        setTagName(initialTagName);
        dialog.show();
      },
      hideModal: () => {
        dialog.hide();
      },
    };
  }, [dialog]);

  useEffect(() => {
    if (tagNameError) {
      setTagNameError(tagName ? undefined : TAG_NAME_REQUIRED);
    }
  }, [tagName, tagNameError]);

  const handleClose = useCallback(() => {
    setTagName('');
    setTagNameError(undefined);
    dialog.hide();
  }, [dialog]);

  const addTagMutation = useMutation(addTag(tagName), {
    onSuccess: () => {
      void queryClient.invalidateQueries([`benefit-tags`]);
      toast.success('Tag created successfully');
      handleClose();
    },
    onError: () => {
      toast.error('Error creating tag');
    },
  });

  const handleCreateTag = useCallback(() => {
    if (!tagName) {
      setTagNameError(TAG_NAME_REQUIRED);
      return;
    }
    setTagNameError(undefined);
    addTagMutation.mutate();
  }, [addTagMutation, tagName]);
  const open = dialog.useState('open');
  return (
    <StyledDialog store={dialog} aria-modal="true">
      {open && (
        <Container>
          <Typography variant={TypographyVariant.h2}>Create Tag</Typography>
          <Input
            name="Name*"
            placeholder="Enter a name"
            showLabel
            value={tagName}
            type="text"
            width="100%"
            onChange={(event) => {
              setTagName(event.target.value);
            }}
            hasError={Boolean(tagNameError)}
            errorMessage={tagNameError}
          />
          <ButtonsContainer>
            <Button
              variant={ButtonVariant.secondary}
              label="Cancel"
              onClick={handleClose}
              width="124px"
            />
            <Button
              variant={ButtonVariant.primary}
              label="Create tag"
              onClick={handleCreateTag}
              width="195px"
            />
          </ButtonsContainer>
        </Container>
      )}
    </StyledDialog>
  );
});

AddTagModal.displayName = 'AddTagModal';

const StyledDialog = styled(Dialog)`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    background: ${theme.palette.black(0.5)};
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${theme.spacing(5)};
  `}
`;

const Container = styled.div`
  ${({ theme }) => css`
    width: 423px;
    height: fit-content;
    padding: ${theme.spacing(5, 5)};
    position: relative;
    background-color: ${theme.palette.neutral['00']};
    border-radius: 10px;
    max-height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `}
`;

const ButtonsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(3)};
    width: 100%;
    margin-top: ${theme.spacing(1)};
    justify-content: center;
  `}
`;

export default memo(AddTagModal);
